/**
 * ReviewForm - Step 6 of CD Creation Wizard
 *
 * Final review step that displays:
 * - Summary of all entered data
 * - Validation warnings/errors
 * - PDF generation options (draft vs final)
 * - Edit links to go back to specific steps
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Edit2,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type {
  CalculatedTotals,
  LineItem,
  LoanTerms,
  Party,
  Transaction,
  ValidationWarning,
} from '../../types';
import { formatCurrency } from './shared';

/** Reconstructed seller credits data from line items */
interface ReconstructedSellerCredits {
  depositAmount: number | null;
  sellerConcessions: number | null;
  propertyTaxProration: number | null;
  hoaProration: number | null;
}

/**
 * Reconstruct seller credits data from section L line items
 */
function reconstructSellerCreditsFromLineItems(lineItems?: LineItem[]): ReconstructedSellerCredits {
  const result: ReconstructedSellerCredits = {
    depositAmount: null,
    sellerConcessions: null,
    propertyTaxProration: null,
    hoaProration: null,
  };

  if (!lineItems || lineItems.length === 0) return result;

  const sectionLItems = lineItems.filter((item) => item.section === 'L');

  for (const item of sectionLItems) {
    const desc = item.description?.toLowerCase() || '';
    const lineNum = item.lineNumberString || '';

    // Deposit/Earnest Money (L01)
    if (desc.includes('deposit') || desc.includes('earnest money') || lineNum === 'L01') {
      result.depositAmount = item.borrowerPaidAtClosing || null;
    }
    // Seller Concessions (L02)
    else if (desc.includes('seller concession') || lineNum === 'L02') {
      result.sellerConcessions = item.sellerPaidAtClosing || null;
    }
    // Property Tax Proration (L03)
    else if (desc.includes('property tax proration') || lineNum === 'L03') {
      result.propertyTaxProration = item.sellerPaidAtClosing || null;
    }
    // HOA Proration (L04)
    else if (desc.includes('hoa proration') || lineNum === 'L04') {
      result.hoaProration = item.sellerPaidAtClosing || null;
    }
  }

  return result;
}

interface ReviewFormProps {
  transaction?: Partial<Transaction>;
  loanTerms?: Partial<LoanTerms>;
  parties?: Party[];
  lineItems?: LineItem[];
  calculations?: Partial<CalculatedTotals>;
  onNext: (data: Record<string, unknown>) => void;
  onBack: () => void;
  onGoToStep: (step: number) => void;
  onGeneratePDF: (version: 'draft' | 'final') => void;
  onPreview?: () => void;
  isLoading?: boolean;
  onRecalculate?: () => void;
  isCalculating?: boolean;
}

/** Calculate validation warnings */
function validateData(
  transaction?: Partial<Transaction>,
  loanTerms?: Partial<LoanTerms>,
  parties?: Party[],
  lineItems?: LineItem[],
): { warnings: ValidationWarning[]; errors: ValidationWarning[] } {
  const warnings: ValidationWarning[] = [];
  const errors: ValidationWarning[] = [];

  // Transaction validation
  if (!transaction?.property_address) {
    errors.push({
      severity: 'error',
      field: 'property_address',
      message: 'Property address is required',
    });
  }
  if (!transaction?.closing_date) {
    warnings.push({
      severity: 'warning',
      field: 'closing_date',
      message: 'Closing date is not set',
    });
  }

  // Loan terms validation
  if (!loanTerms?.loan_amount || loanTerms.loan_amount <= 0) {
    errors.push({
      severity: 'error',
      field: 'loan_amount',
      message: 'Loan amount is required',
    });
  }
  if (!loanTerms?.interest_rate || loanTerms.interest_rate <= 0) {
    errors.push({
      severity: 'error',
      field: 'interest_rate',
      message: 'Interest rate is required',
    });
  }

  // Parties validation
  const borrowers = parties?.filter((p) => p.party_type === 'borrower') ?? [];
  if (borrowers.length === 0) {
    errors.push({
      severity: 'error',
      field: 'parties',
      message: 'At least one borrower is required',
    });
  }
  const primaryBorrower = borrowers.find((b) => b.is_primary);
  if (borrowers.length > 0 && !primaryBorrower) {
    warnings.push({
      severity: 'warning',
      field: 'parties',
      message: 'No primary borrower designated',
    });
  }

  // Line items validation
  if (!lineItems || lineItems.length === 0) {
    warnings.push({
      severity: 'warning',
      field: 'lineItems',
      message: 'No closing costs entered',
    });
  }

  return { warnings, errors };
}

/** Group parties by type for display */
function groupPartiesByType(parties?: Party[]): Map<string, Party[]> {
  const grouped = new Map<string, Party[]>();
  if (!parties) return grouped;

  parties.forEach((party) => {
    const existing = grouped.get(party.party_type) ?? [];
    grouped.set(party.party_type, [...existing, party]);
  });
  return grouped;
}

/** Get human-readable party type label */
function getPartyTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    borrower: 'Borrower(s)',
    seller: 'Seller(s)',
    lender: 'Lender',
    mortgage_broker: 'Mortgage Broker',
    real_estate_broker_buyer: "Buyer's Real Estate Broker",
    real_estate_broker_seller: "Seller's Real Estate Broker",
    settlement_agent: 'Settlement Agent',
  };
  return labels[type] ?? type;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  transaction,
  loanTerms,
  parties,
  lineItems,
  calculations,
  onNext,
  onBack,
  onGoToStep,
  onGeneratePDF,
  onPreview,
  isLoading,
  onRecalculate,
  isCalculating,
}) => {
  // Validate data
  const validation = useMemo(
    () => validateData(transaction, loanTerms, parties, lineItems),
    [transaction, loanTerms, parties, lineItems],
  );

  const hasErrors = validation.errors.length > 0;
  const hasWarnings = validation.warnings.length > 0;

  // Group parties
  const groupedParties = useMemo(() => groupPartiesByType(parties), [parties]);

  // Reconstruct seller credits from section L line items
  const sellerCredits = useMemo(
    () => reconstructSellerCreditsFromLineItems(lineItems),
    [lineItems],
  );

  // Calculate closing costs breakdown
  const closingCostsBreakdown = useMemo(() => {
    if (!lineItems) return { loanCosts: 0, otherCosts: 0, total: 0 };

    const loanCosts = lineItems
      .filter((item) => ['A', 'B', 'C'].includes(item.section))
      .reduce(
        (sum, item) =>
          sum +
          (item.borrowerPaidAtClosing ?? 0) +
          (item.borrowerPaidBeforeClosing ?? 0) +
          (item.sellerPaidAtClosing ?? 0) +
          (item.sellerPaidBeforeClosing ?? 0) +
          (item.paidByOthers ?? 0),
        0,
      );

    const otherCosts = lineItems
      .filter((item) => ['E', 'F', 'G', 'H'].includes(item.section))
      .reduce(
        (sum, item) =>
          sum +
          (item.borrowerPaidAtClosing ?? 0) +
          (item.borrowerPaidBeforeClosing ?? 0) +
          (item.sellerPaidAtClosing ?? 0) +
          (item.sellerPaidBeforeClosing ?? 0) +
          (item.paidByOthers ?? 0),
        0,
      );

    return {
      loanCosts,
      otherCosts,
      total: loanCosts + otherCosts,
    };
  }, [lineItems]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Review & Generate Closing Disclosure</h3>
        {onPreview && (
          <Button variant="outline" size="sm" onClick={onPreview}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Preview CD
          </Button>
        )}
      </div>

      {/* Validation Summary */}
      {(hasErrors || hasWarnings) && (
        <div className="space-y-3">
          {hasErrors && (
            <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <AlertCircle className="h-5 w-5" />
                <span>
                  {validation.errors.length} Error{validation.errors.length > 1 ? 's' : ''} Found
                </span>
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {validation.errors.map((error, i) => (
                  <li key={i}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}
          {hasWarnings && (
            <div className="bg-yellow-50 dark:bg-yellow-950/50 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <AlertTriangle className="h-5 w-5" />
                <span>
                  {validation.warnings.length} Warning{validation.warnings.length > 1 ? 's' : ''}
                </span>
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {validation.warnings.map((warning, i) => (
                  <li key={i}>{warning.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {!hasErrors && !hasWarnings && (
        <div className="bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">All required fields are complete</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Details */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Transaction Details</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onGoToStep(1)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Property:</span>
              <span className="font-medium text-right max-w-[60%]">
                {transaction?.property_address || (
                  <span className="text-destructive italic">Not set</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sale Price:</span>
              <span className="font-medium">
                {transaction?.sale_price ? (
                  formatCurrency(transaction.sale_price)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Closing Date:</span>
              <span className="font-medium">
                {transaction?.closing_date || (
                  <span className="text-muted-foreground">Not set</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">File Number:</span>
              <span className="font-medium">
                {transaction?.file_number || <span className="text-muted-foreground">-</span>}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Loan Terms */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Loan Terms</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onGoToStep(2)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Loan Amount:</span>
              <span className="font-medium">
                {loanTerms?.loan_amount ? (
                  formatCurrency(loanTerms.loan_amount)
                ) : (
                  <span className="text-destructive italic">Not set</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Interest Rate:</span>
              <span className="font-medium">
                {loanTerms?.interest_rate ? (
                  `${loanTerms.interest_rate}%`
                ) : (
                  <span className="text-destructive italic">Not set</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Term:</span>
              <span className="font-medium">
                {loanTerms?.loan_term_months ? (
                  `${loanTerms.loan_term_months} months`
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Loan Type:</span>
              <span className="font-medium capitalize">
                {loanTerms?.loan_type || <span className="text-muted-foreground">-</span>}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly P&I:</span>
              <span className="font-medium">
                {loanTerms?.monthly_principal_and_interest ? (
                  formatCurrency(loanTerms.monthly_principal_and_interest)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Parties */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Parties</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onGoToStep(3)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {groupedParties.size === 0 ? (
              <p className="text-muted-foreground italic">No parties added</p>
            ) : (
              Array.from(groupedParties.entries()).map(([type, partyList], index) => (
                <div key={type}>
                  {index > 0 && <Separator className="my-2" />}
                  <div className="text-muted-foreground text-sm">{getPartyTypeLabel(type)}:</div>
                  {partyList.map((party, i) => (
                    <p key={i} className="font-medium">
                      {party.name}
                      {party.is_primary && (
                        <span className="text-xs text-primary ml-1">(Primary)</span>
                      )}
                    </p>
                  ))}
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Closing Costs Summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Closing Costs</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onGoToStep(4)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Loan Costs (A+B+C):</span>
              <span className="font-medium">
                {formatCurrency(calculations?.totalLoanCosts ?? closingCostsBreakdown.loanCosts)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Other Costs (E+F+G+H):</span>
              <span className="font-medium">
                {formatCurrency(calculations?.totalOtherCosts ?? closingCostsBreakdown.otherCosts)}
              </span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-semibold">
              <span>Total Closing Costs:</span>
              <span>
                {formatCurrency(calculations?.totalClosingCosts ?? closingCostsBreakdown.total)}
              </span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Line items:</span>
              <span>{lineItems?.length ?? 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Seller Credits */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Credits & Adjustments</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onGoToStep(5)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Deposit/Earnest Money:</span>
              <span className="font-medium">
                {sellerCredits.depositAmount ? (
                  formatCurrency(sellerCredits.depositAmount)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Seller Concessions:</span>
              <span className="font-medium">
                {sellerCredits.sellerConcessions ? (
                  formatCurrency(sellerCredits.sellerConcessions)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Property Tax Proration:</span>
              <span className="font-medium">
                {sellerCredits.propertyTaxProration ? (
                  formatCurrency(sellerCredits.propertyTaxProration)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">HOA Proration:</span>
              <span className="font-medium">
                {sellerCredits.hoaProration ? (
                  formatCurrency(sellerCredits.hoaProration)
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Cash to Close Summary */}
        <Card className="bg-muted">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Cash to Close</CardTitle>
            {onRecalculate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRecalculate}
                disabled={isCalculating}
                className="h-8 w-8 p-0"
                title="Refresh calculations"
              >
                {isCalculating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-2xl">
              <span className="text-muted-foreground">Estimated:</span>
              <span className="font-bold text-green-600 dark:text-green-400">
                {formatCurrency(calculations?.cashToClose ?? 0)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              This includes down payment, closing costs, minus credits and adjustments.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Generation Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Closing Disclosure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Generate your Closing Disclosure document. Choose between a draft for review or a
              final document ready for closing.
            </p>

            {hasErrors && (
              <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded text-sm">
                <strong>Cannot generate final document:</strong> Please fix all errors before
                generating a final Closing Disclosure.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => onGeneratePDF('draft')}
                disabled={isLoading}
                className="h-auto py-4"
              >
                <div className="text-left">
                  <div className="font-semibold mb-1">Generate Draft</div>
                  <div className="text-xs text-muted-foreground">
                    For review and internal use. Watermarked.
                  </div>
                </div>
              </Button>

              <Button
                onClick={() => onGeneratePDF('final')}
                disabled={isLoading || hasErrors}
                className="h-auto py-4"
              >
                <div className="text-left">
                  <div className="font-semibold mb-1">Generate Final</div>
                  <div className="text-xs">Official document ready for closing</div>
                </div>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-6">
        <Button type="button" variant="outline" onClick={onBack}>
          Back to Seller Credits
        </Button>
        <Button variant="secondary" onClick={() => onNext({})}>
          Save Draft
        </Button>
      </div>
    </div>
  );
};

export default ReviewForm;
